import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { Footer, Nav } from "../components/lidoo/chrome";
import { ClubCard, priceLine } from "../components/lidoo/club-card";
import { ClubPlate } from "../components/lidoo/club-plate";
import { PhotoGallery } from "../components/lidoo/photo-gallery";
import { StaticClubMap } from "../components/lidoo/static-map";
import { PhotoUpload } from "../components/lidoo/photo-upload";
import { ClubSocials } from "../components/lidoo/club-socials";
import { ShareButton } from "../components/lidoo/share-button";
import { RequestPanel, StickyContactBar } from "../components/lidoo/request-form";
import { GoogleRating, RatingSummary, ReviewsSection } from "../components/lidoo/reviews";
import {
  clubGallery,
  getClubBySlug,
  type ClubSocial,
  getClubReviews,
  relatedClubs,
  type Club,
} from "../lib/api/lidoo.functions";
import { facilityLabel, normalizeFacilities } from "../lib/facilities";
import { DICTS, fill, regionName, useI18n } from "../lib/i18n";
import { NOINDEX_META } from "../lib/indexing";
import { locationLine, openingHours, priceUnit, shortDate } from "../lib/place";
import { catalogueHreflang } from "../lib/seo";
import { clubTitle } from "../lib/titles";

export const Route = createFileRoute("/club/$slug")({
  loader: async ({ params }) => {
    const [{ club, socials }, { reviews }, { photos }, related] = await Promise.all([
      getClubBySlug({ data: { slug: params.slug } }),
      getClubReviews({ data: { clubSlug: params.slug } }),
      clubGallery({ data: { slug: params.slug } }),
      relatedClubs({ data: { slug: params.slug } }),
    ]);
    if (!club) throw notFound();
    return { club, socials, reviews, photos, related: related.clubs };
  },
  head: (ctx) => {
    const club = ctx.loaderData?.club;
    // Name, town and what it is, within 60 characters; see lib/titles.ts.
    const title = club
      ? clubTitle({
          name: club.name,
          town: club.town,
          typeWord: DICTS.en.club_title_type,
          typeInTown: fill(DICTS.en.club_title_in, { town: club.town }),
        })
      : "Beach club | sunbedly";
    const description = club
      ? `${club.name} in ${club.town}, ${club.region_label}. ${
          club.price_from != null
            ? `From ${club.currency} ${club.price_from}${club.price_unit ? ` (${club.price_unit})` : ""}.`
            : "Prices, amenities and direct booking requests."
        } ${club.description}`.slice(0, 300)
      : "Beach club on sunbedly.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        // A club page without its own written description is a facts card:
        // name, place, hours, rating — the same shape a thousand scraped
        // directories serve. Keeping those out of the index until they carry
        // original text shrinks the templated surface Google judges the whole
        // site on. The page itself stays live and fully usable.
        ...(club && !club.long_description ? [NOINDEX_META] : []),
      ],
      links: catalogueHreflang(`/club/${ctx.params.slug}`),
    };
  },
  component: () => <ClubPage {...Route.useLoaderData()} />,
});

export type ClubPageData = {
  club: Club;
  socials: ClubSocial[];
  reviews: Awaited<ReturnType<typeof getClubReviews>>["reviews"];
  photos: Awaited<ReturnType<typeof clubGallery>>["photos"];
  related: Club[];
};

// Props rather than Route.useLoaderData(), so /nl/club/... renders this exact
// page from its own loader instead of the English route's data.
export function ClubPage({ club, socials, reviews, photos, related }: ClubPageData) {
  const { t, lang } = useI18n();
  const facilities = normalizeFacilities(club.facilities, club.amenities);
  const unitDetail = priceUnit(club.price_unit, t).detail;

  // Only questions whose answer is this club's own data: the published rate
  // and the opening hours. The block used to carry five questions on every
  // page, three of them ("do I need to book", "how do I contact", "where is
  // it") answered with the same sentence on all 4,800 pages: a template FAQ
  // with FAQPage markup, which is a spam-classifier pattern rather than help.
  // A club without a rate or hours has no FAQ, and no FAQ schema either.
  const clubFaq: { q: string; a: string }[] = [
    ...(club.price_from != null
      ? [
          {
            q: t("cfaq_q_price", { club: club.name }),
            a: t("cfaq_a_price", { club: club.name, price: priceLine(club, t) }),
          },
        ]
      : []),
    ...(club.opening_hours
      ? [{ q: t("cfaq_q_hours", { club: club.name }), a: openingHours(club.opening_hours, t) ?? "" }]
      : []),
  ];

  const jsonLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    name: club.name,
    description: club.long_description ?? club.description,
    ...(club.opening_hours ? { openingHours: club.opening_hours } : {}),
    ...(facilities.length > 0
      ? {
          amenityFeature: facilities.map((f) => ({
            "@type": "LocationFeatureSpecification",
            name: facilityLabel(f, t),
            value: true,
          })),
        }
      : {}),
    address: {
      "@type": "PostalAddress",
      addressLocality: club.town,
      addressRegion: club.region_label,
      addressCountry: club.country,
    },
    ...(photos.length > 0 ? { image: photos.slice(0, 5).map((p) => p.src) } : {}),
    ...(club.lat != null && club.lng != null
      ? { geo: { "@type": "GeoCoordinates", latitude: club.lat, longitude: club.lng } }
      : {}),
    ...(club.price_from != null
      ? {
          priceRange: `${club.currency} ${club.price_from}`,
          offers: {
            "@type": "Offer",
            price: club.price_from,
            priceCurrency: club.currency,
          },
        }
      : {}),
    ...(club.avg_rating != null && club.review_count > 0
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: club.avg_rating,
            reviewCount: club.review_count,
          },
          review: reviews.slice(0, 20).map((r) => ({
            "@type": "Review",
            author: { "@type": "Person", name: r.author_name },
            reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
            ...(r.title ? { name: r.title } : {}),
            reviewBody: r.body,
          })),
        }
      : {}),
  };

  return (
    <div className="min-h-dvh bg-[#F2EFE9]">
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { name: t("crumb_home"), item: "https://sunbedly.com/" },
              { name: t("nav_explore"), item: "https://sunbedly.com/clubs" },
              { name: club.region_label, item: `https://sunbedly.com/region/${club.region_key}` },
              { name: club.name, item: `https://sunbedly.com/club/${club.slug}` },
            ].map((e, i) => ({ "@type": "ListItem", position: i + 1, name: e.name, item: e.item })),
          }),
        }}
      />
      <Nav />
      <main className="mx-auto max-w-6xl px-5 pb-20">
        {/* A real trail rather than a single back link: it tells a visitor
            where on the coast they are and gives the crawler the hierarchy. */}
        <nav aria-label="Breadcrumb" className="li-mono flex flex-wrap items-center gap-1.5 pt-8 text-[12px] text-[#5B6472]">
          <Link to="/" className="hover:text-[#2C4FD6]">
            {t("crumb_home")}
          </Link>
          <span aria-hidden="true">/</span>
          <Link to="/clubs" className="hover:text-[#2C4FD6]">
            {t("nav_explore")}
          </Link>
          <span aria-hidden="true">/</span>
          <Link
            to="/region/$key"
            params={{ key: club.region_key }}
            className="hover:text-[#2C4FD6]"
          >
            {regionName(club.region_key, club.region_label, t)}
          </Link>
          <span aria-hidden="true">/</span>
          <span className="text-[#1C2430]">{club.name}</span>
        </nav>

        <div className="mt-6 grid gap-10 lg:grid-cols-[1.4fr_1fr]">
          <div>
            {/* Own photos, then Google's, then the plate: the gallery gets the
                plate as its fallback too, so photos that all fail to load end
                in the same place as having none. */}
            <PhotoGallery
              photos={photos}
              name={club.name}
              town={club.town}
              fallback={
                <div className="h-[300px] overflow-hidden rounded-2xl md:h-[440px]">
                  <ClubPlate club={club} variant="hero" />
                </div>
              }
            />
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <h1 className="li-serif text-3xl font-normal  text-[#1C2430] md:text-5xl">
                {club.name}
              </h1>
              <span
                className={`li-mono rounded-md px-2 py-1 text-[10px] uppercase tracking-wider ${
                  club.verified ? "bg-[#2C4FD6] text-[#F2EFE9]" : "bg-[#E7E2D6] text-[#5B6472]"
                }`}
              >
                {club.verified ? t("verified") : t("unverified")}
              </span>
              <RatingSummary avg={club.avg_rating} count={club.review_count} />
              <GoogleRating
                rating={club.google_rating}
                count={club.google_review_count}
                size="lg"
              />
            </div>
            <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
              <p className="text-base text-[#5B6472]">{locationLine(club, t)}</p>
              <ShareButton name={club.name} town={club.town} />
            </div>
            {/* "Checked by sunbedly on 17/07/2026" rather than a bare date.
                Most of these 827 listings we compiled ourselves, and a visible
                name plus a date is what turns a compiled entry into a checked
                one. It is the cheapest trust signal on the page. */}
            {club.researched_at || club.season_note ? (
              <p className="li-mono mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-[#5B6472]">
                {club.researched_at ? (
                  <span className="inline-flex items-center gap-1">
                    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 text-[#1F7A6B]" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M3 8.5 6.5 12 13 4.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {fill(t("updated_by"), { date: shortDate(club.researched_at, lang) })}
                  </span>
                ) : null}
                {club.researched_at && club.season_note === "may_oct" ? (
                  <span aria-hidden="true">/</span>
                ) : null}
                {club.season_note === "may_oct" ? <span>{t("season_may_oct")}</span> : null}
              </p>
            ) : null}
            <p className="li-mono mt-4 text-[14px] text-[#1C2430]">{priceLine(club, t)}</p>
            {/* What the club itself says the rate covers: rows, seasons,
                minimum spend, what a towel costs. Too long for the price line,
                too useful to throw away. */}
            {unitDetail ? (
              <p className="li-mono mt-1 max-w-[52ch] text-[11px] leading-relaxed text-[#5B6472]">
                {t("pu_detail")}: {unitDetail}
              </p>
            ) : null}

            {/* On mobile the request panel sits here, directly under the price,
                rather than at the very bottom of a 8000px page where the
                measured position of the only contact button was y=2955. On
                desktop the same panel rides the sidebar. */}
            <div className="mt-6 lg:hidden">
              <RequestPanel club={club} />
            </div>

            {/* The "good for" pill used to sit here. Removed: research filled
                it with anything from "Families" to a whole sentence, so it read
                as clutter rather than a signal. The field stays in the database
                for filtering later, it just is not a badge. */}

            {/* About: the researched long-form write-up when we have one, the
                one-line summary otherwise. */}
            <section className="mt-8">
              <h2 className="li-display text-xl font-bold tracking-tight text-[#1C2430]">
                {t("about_title")}
              </h2>
              <div className="mt-3 max-w-[68ch] space-y-4 text-base leading-relaxed text-[#1C2430]/90">
                {(club.long_description ?? club.description)
                  .split(/\n{2,}/)
                  .map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
              </div>
            </section>

            {facilities.length > 0 ? (
              <section className="mt-8">
                <h2 className="li-display text-xl font-bold tracking-tight text-[#1C2430]">
                  {t("facilities_title")}
                </h2>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {/* One merged, translated set. The researched strings and the
                      fixed amenity tags used to render as two separate lists,
                      which put "Beach towels" next to "Teli mare inclusi" on
                      the same club. */}
                  {facilities.map((f) => (
                    <li
                      key={f}
                      className="li-mono rounded-full border border-[#1C2430]/15 bg-white/60 px-3 py-1.5 text-[12px] text-[#1C2430]"
                    >
                      {facilityLabel(f, t)}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <section className="mt-8 grid gap-6 sm:grid-cols-2">
              <div>
                <h2 className="li-display text-xl font-bold tracking-tight text-[#1C2430]">
                  {t("hours_title")}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-[#5B6472]">
                  {openingHours(club.opening_hours, t) ?? t("hours_unknown")}
                </p>
              </div>
              {club.rules ? (
                <div>
                  <h2 className="li-display text-xl font-bold tracking-tight text-[#1C2430]">
                    {t("rules_title")}
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-[#5B6472]">{club.rules}</p>
                </div>
              ) : null}
            </section>
            {club.lat != null && club.lng != null ? (
              <div className="mt-8">
                <h2 className="li-display text-lg font-bold tracking-tight text-[#1C2430]">
                  {t("route_title")}
                </h2>
                <div className="mt-3">
                  <StaticClubMap lat={club.lat} lng={club.lng} name={club.name} />
                </div>
                {club.approx_location ? (
                  <p className="li-mono mt-2 text-[11px] text-[#5B6472]">{t("approx_note")}</p>
                ) : null}
              </div>
            ) : club.approx_location ? (
              <p className="li-mono mt-6 text-[11px] text-[#5B6472]">{t("approx_note")}</p>
            ) : null}
            {/* The club's own site, measured, but placed here rather than in
                the request panel. It belongs on the page: every click is
                evidence we can show that club later. It does not belong next
                to the WhatsApp button, where it was one more exit competing
                with the single action this page exists for. */}
            {club.website ? (
              <p className="mt-8">
                <a
                  href={`/go/${club.slug}`}
                  target="_blank"
                  rel="noopener nofollow"
                  className="li-mono text-[12px] text-[#2C4FD6] underline underline-offset-4"
                >
                  {t("contact_website")} -&gt;
                </a>
              </p>
            ) : null}

            <ClubSocials socials={socials} slug={club.slug} />

            <div className="mt-8 rounded-xl border border-dashed border-[#1C2430]/25 p-5">
              <p className="text-sm text-[#5B6472]">{t("claim_note")}</p>
              <Link
                to="/partner"
                className="li-mono mt-2 inline-block text-[12px] text-[#2C4FD6] underline underline-offset-4"
              >
                {t("claim_cta")}
              </Link>
            </div>

            <div className="mt-6">
              <PhotoUpload clubSlug={club.slug} />
            </div>
          </div>

          {/* The booking column follows the reader down the page: on a listing
              this long the contact details would otherwise scroll away just as
              someone decides they want the place. */}
          <div className="hidden lg:sticky lg:top-24 lg:block lg:self-start lg:pt-2">
            <RequestPanel club={club} id="request-desktop" />
          </div>
        </div>

        {/* Ratings side by side. Google's number is shown attributed, never
            restated as ours, and only when the Places sync has actually run. */}
        {club.google_rating != null && club.google_review_count ? (
          <section className="mt-14 rounded-2xl border border-[#1C2430]/10 bg-white/60 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="li-mono text-[11px] uppercase tracking-widest text-[#5B6472]">
                  {t("reviews_sunbedly")}
                </p>
                <p className="li-display mt-1 text-2xl font-black tracking-tighter text-[#1C2430]">
                  {club.avg_rating != null ? `${club.avg_rating} / 5` : "—"}{" "}
                  <span className="li-mono text-[12px] font-normal text-[#5B6472]">
                    ({club.review_count})
                  </span>
                </p>
              </div>
              <div className="sm:border-l sm:border-[#1C2430]/10 sm:pl-4">
                <p className="li-mono text-[11px] uppercase tracking-widest text-[#5B6472]">
                  {t("reviews_google")}
                </p>
                <p className="li-display mt-1 text-2xl font-black tracking-tighter text-[#1C2430]">
                  {club.google_rating} / 5{" "}
                  <span className="li-mono text-[12px] font-normal text-[#5B6472]">
                    ({club.google_review_count})
                  </span>
                </p>
              </div>
            </div>
          </section>
        ) : null}

        <ReviewsSection
          clubSlug={club.slug}
          initialReviews={reviews}
          avg={club.avg_rating}
          count={club.review_count}
        />

        {/* Three alternatives on the same coast. A visitor who bounces off this
            club should land on another one of ours, not back on Google. */}
        {related.length > 0 ? (
          <section className="mt-16 border-t border-[#1C2430]/15 pt-10">
            <h2 className="li-serif text-2xl text-[#1C2430] md:text-3xl">{t("nearby_title")}</h2>
            <p className="mt-1 text-sm text-[#5B6472]">{t("nearby_sub")}</p>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((c) => (
                <ClubCard key={c.slug} club={c} />
              ))}
            </div>
          </section>
        ) : null}

        {/* Per-club FAQ, built from this club's own data only (see clubFaq
            above). Absent when there is nothing club-specific to answer. */}
        {clubFaq.length > 0 ? (
        <section className="mt-14">
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: clubFaq.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              }),
            }}
          />
          <h2 className="li-serif text-2xl font-normal  text-[#1C2430] md:text-3xl">
            {t("club_faq_title", { club: club.name })}
          </h2>
          <dl className="mt-6 divide-y divide-[#1C2430]/10 border-y border-[#1C2430]/10">
            {clubFaq.map((f) => (
              <div key={f.q} className="py-5">
                <dt className="li-display text-base font-bold tracking-tight text-[#1C2430]">
                  {f.q}
                </dt>
                <dd className="mt-2 max-w-[68ch] text-sm leading-relaxed text-[#5B6472]">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>
        ) : null}
      </main>
      <Footer />
      <StickyContactBar club={club} priceText={priceLine(club, t)} />
      {/* Room for the sticky bar so it never covers the footer's last line. */}
      <div className="h-16 lg:hidden" aria-hidden="true" />
    </div>
  );
}
